import { useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { paymentActions } from "../store/payment.slice";

const FLUSH_INTERVAL_MS = 5000;
const RETRY_INTERVAL_MS = 10000;
const MAX_RETRIES = 3;
const STORAGE_KEY = "hg_telemetry_retry_queue";

const TERMINAL_EVENTS = new Set([
  "payment_confirmation_succeeded",
  "payment_confirmation_failed",
  "payment_element_load_error",
  "create_payment_intent_network_error",
  "create_payment_intent_no_response",
  "create_payment_intent_failed",
  "update_payment_status_completed",
  "component_unmounted",
]);

const NO_DEDUPE = new Set([
  "payment_element_change",
  "payment_element_focus",
  "payment_element_blur",
  "submit_button_clicked",
  "cancel_button_clicked",
]);

function savePendingBatch(events) {
  try {
    const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    existing.push({ events, retries: 0 });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
  } catch {}
}

function getPendingBatches() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function setPendingBatches(batches) {
  try {
    if (batches.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(batches));
    }
  } catch {}
}

function flushBeacon(events) {
  if (!navigator.sendBeacon || events.length === 0) return;
  const TELEMETRY_ENDPOINT = `${process.env.REACT_APP_API_URL}/payment-telemetry`;
  navigator.sendBeacon(
    TELEMETRY_ENDPOINT,
    new Blob([JSON.stringify({ events })], { type: "application/json" })
  );
}

function generateSessionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getBrowserInfo() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    online: navigator.onLine,
    connectionType: navigator.connection?.effectiveType ?? null,
    pageProtocol: window.location.protocol,
  };
}

function getDeviceInfo() {
  const width = window.screen?.width ?? 0;
  const height = window.screen?.height ?? 0;
  return { screenWidth: width, screenHeight: height, isMobile: width <= 768 };
}

export function usePaymentTelemetry(bookingSlug) {
  const dispatch = useDispatch();
  const sessionIdRef = useRef(generateSessionId());
  const sentEventsRef = useRef(new Set());
  const browserInfoRef = useRef(getBrowserInfo());
  const deviceInfoRef = useRef(getDeviceInfo());
  const queueRef = useRef([]);

  const retryPending = useCallback(async () => {
    const pending = getPendingBatches();
    if (pending.length === 0) return;

    const remaining = [];
    for (const batch of pending) {
      if (batch.retries >= MAX_RETRIES) continue;
      try {
        const result = await dispatch(
          paymentActions.savePaymentTelemetry({ events: batch.events })
        );
        if (result?.error) {
          remaining.push({ ...batch, retries: batch.retries + 1 });
        }
      } catch {
        remaining.push({ ...batch, retries: batch.retries + 1 });
      }
    }

    setPendingBatches(remaining);
  }, [dispatch]);

  const flushQueue = useCallback(
    async (queue) => {
      if (queue.length === 0) return;
      try {
        const result = await dispatch(
          paymentActions.savePaymentTelemetry({ events: queue })
        );
        if (result?.error) {
          savePendingBatch(queue);
        }
      } catch {
        savePendingBatch(queue);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const flushTimer = setInterval(() => {
      if (queueRef.current.length > 0) {
        flushQueue([...queueRef.current]);
        queueRef.current = [];
      }
    }, FLUSH_INTERVAL_MS);

    const retryTimer = setInterval(() => {
      retryPending();
    }, RETRY_INTERVAL_MS);

    const handleUnload = () => {
      flushBeacon([...queueRef.current]);
      queueRef.current = [];
    };
    window.addEventListener("beforeunload", handleUnload);

    retryPending();

    return () => {
      clearInterval(flushTimer);
      clearInterval(retryTimer);
      window.removeEventListener("beforeunload", handleUnload);
      flushQueue([...queueRef.current]);
      queueRef.current = [];
    };
  }, [flushQueue, retryPending]);

  const resetSession = useCallback(() => {
    sessionIdRef.current = generateSessionId();
    sentEventsRef.current = new Set();
    browserInfoRef.current = getBrowserInfo();
  }, []);

  const logEvent = useCallback(
    (eventName, status, metadata = {}) => {
      if (!NO_DEDUPE.has(eventName)) {
        const dedupeKey = `${eventName}:${status}`;
        if (sentEventsRef.current.has(dedupeKey)) return;
        sentEventsRef.current.add(dedupeKey);
      }

      const { error, ...restMetadata } = metadata;

      queueRef.current.push({
        session_id: sessionIdRef.current,
        reservation_code: bookingSlug ?? null,
        timestamp: new Date().toISOString(),
        event_name: eventName,
        event_status: status,
        browser: browserInfoRef.current,
        device: deviceInfoRef.current,
        error: error ?? null,
        metadata: restMetadata,
      });

      if (TERMINAL_EVENTS.has(eventName)) {
        flushQueue([...queueRef.current]);
        queueRef.current = [];
      }
    },
    [bookingSlug, flushQueue]
  );

  return { logEvent, resetSession };
}
